/**
 * Authentication service that abstracts auth operations from Firebase to FastAPI.
 * Provides a clean interface for authentication without exposing implementation details.
 */

import apiClient, { ApiResponse } from './client';
import { UserData } from '@/types/user';

export interface AuthUser {
  id: string;
  email: string | null;
  displayName: string | null;
  email_verified: boolean;
  photoURL: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  user: AuthUser;
  access_token: string;
  refresh_token?: string;
  session_id?: string;
  session_expires_at?: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  display_name?: string;
  invitation_token?: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface GoogleSignInRequest {
  id_token: string;
  invitation_token?: string;
  context?: 'login' | 'signup';
}

export interface UserUpdateRequest {
  display_name?: string;
  photo_url?: string;
  timezone?: string;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
}

export interface SupabasePasswordUpdateRequest {
  access_token: string;
  new_password: string;
  refresh_token?: string;
}

export interface ExtensionAuthTokenRequest {
  organization_id: string;
}

export interface ExtensionAuthTokenResponse {
  token_id: string;
  expires_at: string;
}

export interface ExtensionAuthTokenConsumeRequest {
  token_id: string;
}

export interface ExtensionAuthTokenConsumeResponse {
  user_id: string;
  organization_id: string;
}

export interface UserSession {
  session_id: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  last_activity: string;
  expires_at: string;
}

export interface LoginAttempt {
  attempt_id: string;
  email: string;
  success: boolean;
  ip_address?: string;
  user_agent?: string;
  failure_reason?: string;
  attempted_at: string;
}

export interface LockoutInfo {
  lockout_id: string;
  failed_attempt_count: number;
  lockout_reason: string;
  ip_address?: string;
  is_active: boolean;
  locked_at: string;
  locked_until: string;
  unlocked_at?: string;
}

export class AuthService {
  private sessionTimeoutId: NodeJS.Timeout | null = null;
  private sessionExpiryWarningId: NodeJS.Timeout | null = null;
  private onSessionExpiry?: () => void;
  private onSessionExpiryWarning?: (minutesLeft: number) => void;
  /**
   * Sign up a new user with email and password
   */
  async signUp(data: SignUpRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/api/auth/signup', data);
    
    if (response.success && response.data?.access_token) {
      apiClient.setAuthToken(response.data.access_token);
      this.storeAuthToken(response.data.access_token);
      if (response.data.refresh_token) {
        this.storeRefreshToken(response.data.refresh_token);
      }
    }
    
    return response;
  }

  /**
   * Sign in with email and password
   */
  async signIn(data: SignInRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/api/auth/signin', data);
    
    if (response.success && response.data?.access_token) {
      apiClient.setAuthToken(response.data.access_token);
      this.storeAuthToken(response.data.access_token);
      if (response.data.refresh_token) {
        this.storeRefreshToken(response.data.refresh_token);
      }
      
      // Set up session timeout if session_expires_at is provided
      if (response.data.session_expires_at) {
        this.setupSessionTimeout(new Date(response.data.session_expires_at));
      }
    }
    
    return response;
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle(data: GoogleSignInRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<AuthResponse>('/api/auth/signin/google', data);
    
    if (response.success && response.data?.access_token) {
      apiClient.setAuthToken(response.data.access_token);
      this.storeAuthToken(response.data.access_token);
      if (response.data.refresh_token) {
        this.storeRefreshToken(response.data.refresh_token);
      }
    }
    
    return response;
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<ApiResponse<AuthUser>> {
    return apiClient.get<AuthUser>('/api/auth/me');
  }

  /**
   * Update current user information
   */
  async updateUser(data: UserUpdateRequest): Promise<ApiResponse<AuthUser>> {
    return apiClient.put<AuthUser>('/api/auth/me', data);
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post<{ message: string }>('/api/auth/logout');
    
    // Clear tokens and session timeout regardless of response
    this.clearAuthData();
    this.clearSessionTimeout();
    
    return response;
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>('/api/auth/password/reset', data);
  }

  /**
   * Confirm password reset
   */
  async confirmPasswordReset(data: PasswordResetConfirmRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>('/api/auth/password/reset/confirm', data);
  }

  /**
   * Change password
   */
  async changePassword(data: PasswordChangeRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>('/api/users/me/change-password', data);
  }

  /**
   * Update password using Supabase access token
   */
  async updatePasswordWithSupabaseToken(data: SupabasePasswordUpdateRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>('/api/auth/password/update-supabase', data);
  }

  /**
   * Refresh user data
   */
  async refreshUserData(): Promise<ApiResponse<AuthUser>> {
    return apiClient.post<AuthUser>('/api/auth/refresh');
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.delete<{ message: string }>('/api/auth/account');
    
    if (response.success) {
      this.clearAuthData();
    }
    
    return response;
  }

  /**
   * Create extension auth token
   */
  async createExtensionAuthToken(data: ExtensionAuthTokenRequest): Promise<ApiResponse<ExtensionAuthTokenResponse>> {
    return apiClient.post<ExtensionAuthTokenResponse>('/api/auth/extension/token', data);
  }

  /**
   * Consume extension auth token
   */
  async consumeExtensionAuthToken(data: ExtensionAuthTokenConsumeRequest): Promise<ApiResponse<ExtensionAuthTokenConsumeResponse>> {
    return apiClient.post<ExtensionAuthTokenConsumeResponse>('/api/auth/extension/consume', data);
  }

  /**
   * Logout from all sessions
   */
  async logoutAllSessions(): Promise<ApiResponse<{ message: string }>> {
    const response = await apiClient.post<{ message: string }>('/api/auth/logout-all');
    
    if (response.success) {
      this.clearAuthData();
    }
    
    return response;
  }

  /**
   * Get active sessions for current user
   */
  async getUserSessions(): Promise<ApiResponse<{ sessions: UserSession[] }>> {
    return apiClient.get<{ sessions: UserSession[] }>('/api/auth/sessions');
  }

  /**
   * Get login attempts for current user
   */
  async getLoginAttempts(limit: number = 50): Promise<ApiResponse<{ login_attempts: LoginAttempt[] }>> {
    return apiClient.get<{ login_attempts: LoginAttempt[] }>(`/api/auth/login-attempts?limit=${limit}`);
  }

  /**
   * Get lockout history for current user
   */
  async getLockoutHistory(limit: number = 10): Promise<ApiResponse<{ lockout_history: LockoutInfo[] }>> {
    return apiClient.get<{ lockout_history: LockoutInfo[] }>(`/api/auth/lockout-history?limit=${limit}`);
  }

  /**
   * Initialize auth state from stored tokens
   */
  async initializeAuth(): Promise<AuthUser | null> {
    const token = this.getStoredAuthToken();
    
    if (!token) {
      return null;
    }

    apiClient.setAuthToken(token);
    
    const response = await this.getCurrentUser();
    
    if (!response.success) {
      // Token is invalid, clear it
      this.clearAuthData();
      return null;
    }

    return response.data || null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getStoredAuthToken();
  }

  /**
   * Store auth token in localStorage
   */
  private storeAuthToken(token: string): void {
    localStorage.setItem('hobbes_auth_token', token);
  }

  /**
   * Store refresh token in localStorage
   */
  private storeRefreshToken(token: string): void {
    localStorage.setItem('hobbes_refresh_token', token);
  }

  /**
   * Get stored auth token from localStorage
   */
  private getStoredAuthToken(): string | null {
    return localStorage.getItem('hobbes_auth_token');
  }

  /**
   * Get stored refresh token from localStorage
   */
  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('hobbes_refresh_token');
  }

  /**
   * Clear all auth data
   */
  private clearAuthData(): void {
    localStorage.removeItem('hobbes_auth_token');
    localStorage.removeItem('hobbes_refresh_token');
    apiClient.setAuthToken(null);
  }

  /**
   * Set up session timeout based on expiry time
   */
  private setupSessionTimeout(expiryTime: Date): void {
    this.clearSessionTimeout();
    
    const now = new Date();
    const timeUntilExpiry = expiryTime.getTime() - now.getTime();
    
    if (timeUntilExpiry <= 0) {
      // Session already expired
      this.handleSessionExpiry();
      return;
    }
    
    // Set up warning 5 minutes before expiry
    const warningTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before
    if (warningTime > 0) {
      this.sessionExpiryWarningId = setTimeout(() => {
        this.onSessionExpiryWarning?.(5);
      }, warningTime);
    }
    
    // Set up automatic logout at expiry
    this.sessionTimeoutId = setTimeout(() => {
      this.handleSessionExpiry();
    }, timeUntilExpiry);
  }

  /**
   * Clear session timeout timers
   */
  private clearSessionTimeout(): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
    if (this.sessionExpiryWarningId) {
      clearTimeout(this.sessionExpiryWarningId);
      this.sessionExpiryWarningId = null;
    }
  }

  /**
   * Handle session expiry
   */
  private handleSessionExpiry(): void {
    this.clearAuthData();
    this.clearSessionTimeout();
    this.onSessionExpiry?.();
  }

  /**
   * Set session expiry callback
   */
  setSessionExpiryCallback(callback: () => void): void {
    this.onSessionExpiry = callback;
  }

  /**
   * Set session expiry warning callback
   */
  setSessionExpiryWarningCallback(callback: (minutesLeft: number) => void): void {
    this.onSessionExpiryWarning = callback;
  }

  /**
   * Extend current session (called by user activity)
   */
  async extendSession(): Promise<void> {
    const token = this.getStoredAuthToken();
    if (!token) return;
    
    try {
      // The backend will automatically extend session on token validation
      await this.getCurrentUser();
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  }

  /**
   * Convert AuthUser to UserData format for compatibility
   */
  static authUserToUserData(authUser: AuthUser): UserData {
    return {
      uid: authUser.id,
      email: authUser.email,
      displayName: authUser.display_name,
      photoURL: authUser.photo_url,
      createdAt: new Date(authUser.created_at),
      updatedAt: new Date(authUser.updated_at),
      emailVerified: authUser.email_verified,
      // Add any additional fields that might be needed
    };
  }

  /**
   * Convert UserData to AuthUser format
   */
  static userDataToAuthUser(userData: UserData): AuthUser {
    return {
      id: userData.uid,
      email: userData.email,
      display_name: userData.displayName,
      photo_url: userData.photoURL,
      email_verified: userData.emailVerified || false,
      created_at: userData.createdAt instanceof Date ? userData.createdAt.toISOString() : userData.createdAt?.toString() || new Date().toISOString(),
      updated_at: userData.updatedAt instanceof Date ? userData.updatedAt.toISOString() : userData.updatedAt?.toString() || new Date().toISOString(),
    };
  }
}

// Create and export the default auth service instance
const authService = new AuthService();

export default authService;
